import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Scatter, Bubble } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const ChartRenderer = ({ config }) => {
  const chartRef = useRef(null);

  if (!config || !config.type) {
    return null;
  }

  const { type, data, options } = config;
  
  // Validate data structure for Chart.js
  if (!data || !data.labels || !data.datasets || !Array.isArray(data.datasets)) {
    console.warn('ChartRenderer: Invalid data format', data);
    return (
      <Container>
        <ErrorMessage>
          Chart data is not available or malformed
        </ErrorMessage>
      </Container>
    );
  }

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: config.options?.indexAxis || 'x',  // 'y' for horizontal bars
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: !!options?.title,
        text: options?.title || '',
        font: {
          size: 16,
          weight: 'bold',
        },
      },
    },
    ...options,
  };

  const renderChart = () => {
    switch (type.toLowerCase()) {
      case 'bar':
        return <Bar ref={chartRef} data={data} options={defaultOptions} />;
      case 'line':
        return <Line ref={chartRef} data={data} options={defaultOptions} />;
      case 'pie':
        return <Pie ref={chartRef} data={data} options={defaultOptions} />;
      case 'doughnut':
        return <Doughnut ref={chartRef} data={data} options={defaultOptions} />;
      case 'scatter':
        return <Scatter ref={chartRef} data={data} options={defaultOptions} />;
      case 'bubble':
        return <Bubble ref={chartRef} data={data} options={defaultOptions} />;
      case 'wordcloud':
        // Word cloud: render as simple frequency list for now
        // TODO: Add react-wordcloud package for proper word cloud visualization
        return (
          <WordCloudContainer>
            {data && Array.isArray(data) && data.slice(0, 50).map((word, idx) => (
              <WordItem
                key={idx}
                style={{
                  fontSize: `${Math.max(0.8, Math.min(2.5, word.value / 10))}rem`,
                  opacity: Math.max(0.4, Math.min(1, word.value / 50))
                }}
              >
                {word.text}
              </WordItem>
            ))}
          </WordCloudContainer>
        );
      default:
        return (
          <ErrorMessage>
            Unsupported chart type: {type}
          </ErrorMessage>
        );
    }
  };

  return (
    <Container>
      <ChartTitle>{config.title || 'Visualization'}</ChartTitle>
      <ChartWrapper>{renderChart()}</ChartWrapper>
      {config.description && (
        <ChartDescription>{config.description}</ChartDescription>
      )}
    </Container>
  );
};

export default ChartRenderer;

const Container = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ChartTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f0f0;
`;

const ChartWrapper = styled.div`
  width: 100%;
  height: 400px;
  position: relative;
`;

const ChartDescription = styled.p`
  font-size: 0.9rem;
  color: #666;
  margin: 1rem 0 0 0;
  line-height: 1.5;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #dc3545;
  font-size: 0.95rem;
`;

const WordCloudContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 2rem;
  align-items: center;
  justify-content: center;
  min-height: 400px;
`;

const WordItem = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  color: #333;
  font-weight: 500;
  transition: all 0.2s;
  cursor: default;
  
  &:hover {
    color: #007bff;
    transform: scale(1.1);
  }
`;
